import React, { useState } from 'react';
import { loadContingent } from '../../model/contingent';
import { Event } from '../../model/event';
import { useAsync } from '../../model/data';
import { KeyValueTable } from '../KeyValueTable/KeyValueTable';
import { Icon } from '../Icon/Icon';
import { Modal } from '../Modal/Modal';
import { CapacityEditForm } from './CapacityEditForm';
import { t } from '../../locales/translate';
import { Link } from 'react-router-dom';
import { eventPath } from '../../routes';
import { Button } from '../Button/Button';
import { Stack } from '../Stack/Stack';
import { Spacer } from '../Spacer/Spacer';

interface Props {
  section: { id: string };
  event: Event;
  allowEditingCapacity?: boolean;
  linkToContingent?: boolean;
}

export function ContingentList({ section, event, allowEditingCapacity, linkToContingent }: Props): JSX.Element {
  const contingent = event.contingents?.find((e) => e.sectionId === section.id) ?? null;
  useAsync(
    async () => {
      await loadContingent(event, section.id);
    },
    [section.id],
    { handleError: true }
  );
  const [editingCapacity, setEditingCapacity] = useState(false);

  const hasPools = (contingent?.pools?.length ?? 0) > 0;

  if (!contingent) return <Icon loading={true} name="home" />;

  return (
    <>
      <Stack size="xs">
        <h3>{t.model.contingent.capacityAndAvailability}</h3>
        <KeyValueTable
          variant="table"
          labelWidth={300}
          valueWidth={100}
          items={[
            {
              key: t.model.contingent.capacity,
              value: (
                <span style={{ position: 'relative' }}>
                  {contingent?.capacity}
                  {allowEditingCapacity && (
                    <Icon attached name="edit--sm" className="icon--gray" onClick={() => setEditingCapacity(true)} />
                  )}
                </span>
              ),
            },
            {
              key: `${t.model.contingent.issued} / ${t.model.contingent.reserved} / ${t.model.contingent.shoppingCart}`,
              value: contingent.valid + contingent.reserved + contingent.cart,
            },
            { key: t.model.contingent.availableTickets, value: contingent?.available },
          ]}
        />
      </Stack>
      <Spacer />
      <Stack size="xs">
        <h3>
          {t.model.contingent.blocks} / {t.model.contingent.options}
        </h3>
        {hasPools && (
          <KeyValueTable
            items={contingent?.pools?.map((e) => ({ key: `"${e.name}"`, value: e.count })) ?? []}
            variant="table"
            labelWidth={300}
            valueWidth={100}
          />
        )}
        {!hasPools && (
          <>
            {linkToContingent ? (
              <div className="contingent-list__none-created">
                <span>{t.model.contingent.noneCreated}. </span>
                <Link to={`${eventPath(event.slug)}/contingent`}>
                  <Button title={t.model.contingent.createBlocks} type="link" />
                </Link>
              </div>
            ) : (
              <div className="contingent-list__none-created">{t.model.contingent.noBlocksOrOptionsYet}.</div>
            )}
          </>
        )}
        <Modal open={editingCapacity} size="small" title={t.model.contingent.adjustCapacity}>
          {() =>
            contingent && (
              <CapacityEditForm
                event={event}
                section={section}
                onClose={() => setEditingCapacity(false)}
                contingent={contingent}
              />
            )
          }
        </Modal>
      </Stack>
    </>
  );
}
